import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  CarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { getFirestore, doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { format } from 'date-fns';
import { reservationService } from '../firebase/services';
import { 
  getStatusDisplayName, 
  getStatusColor, 
  getStatusIconName,
  getStatusTransitionExplanation
} from '../utils/statusUtils';

const ReservationDetail = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [listingCategories, setListingCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadReservationData = async () => {
      try {
        setLoading(true);
        
        // Get reservation data using service
        const reservationData = await reservationService.getReservationById(reservationId);
        
        // Fetch listing categories from Firestore
        const db = getFirestore();
        const listingCategoriesSnapshot = await getDocs(collection(db, 'listingcategories'));
        const categoriesData = listingCategoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setListingCategories(categoriesData);
        
        setReservation(reservationData);
        setError(null);
      } catch (err) {
        setError('Failed to load reservation details');
        console.error('Error loading reservation data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (reservationId) {
      loadReservationData();
    }
  }, [reservationId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatDuration = (duration, durationUnit) => {
    if (!duration) return 'N/A';
    const unit = durationUnit ? durationUnit.toLowerCase() : '';
    return `${duration} ${unit}`;
  };

  const getVehicleTypeDisplay = (vehicleType, listingCategories) => {
    if (!vehicleType) return 'N/A';
    
    // If listingCategories has index that matches vehicleType, use ContextValue
    if (listingCategories && listingCategories.index === vehicleType) {
      return listingCategories.ContextValue || vehicleType;
    }
    
    // Otherwise return the vehicleType as is
    return vehicleType;
  };

  const getVehicleTypeFromListingCategories = (vehicleType, listingCategories) => {
    if (!vehicleType) return 'N/A';
    
    // If listingCategories is an array, find the matching item
    if (Array.isArray(listingCategories)) {
      const matchingCategory = listingCategories.find(category => category.index === vehicleType);
      return matchingCategory ? matchingCategory.ContextValue : vehicleType;
    }
    
    // If listingCategories is an object with index property
    if (listingCategories && listingCategories.index === vehicleType) {
      return listingCategories.ContextValue || vehicleType;
    }
    
    // If listingCategories is a collection/object with multiple items
    if (listingCategories && typeof listingCategories === 'object') {
      // Try to find a property that matches the vehicleType
      for (const key in listingCategories) {
        const category = listingCategories[key];
        if (category && category.index === vehicleType) {
          return category.ContextValue || vehicleType;
        }
      }
    }
    
    // Otherwise return the vehicleType as is
    return vehicleType;
  };

  const getStatusIcon = (status) => {
    const iconName = getStatusIconName(status);
    switch (iconName) {
      case 'CheckCircleIcon':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'XCircleIcon':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'ExclamationTriangleIcon':
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColorClass = (status) => {
    return getStatusColor(status);
  };

  if (loading) {
    return (
      <div className="h-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading reservation details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="h-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'Reservation not found'}</p>
            <button
              onClick={() => navigate('/reservations')}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              ← Back to Reservations
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/reservations')}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reservation Detail</h1>
              <p className="text-sm text-gray-500 mt-1">
                ID: {reservation.id}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColorClass(reservation.state)}`}>
              {getStatusIcon(reservation.state)}
              <span className="ml-2">{getStatusDisplayName(reservation.state)}</span>
            </span>
          </div>
        </div>

        {/* Reservation Metadata */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2" />
            Reservation Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Reservation ID</label>
              <p className="mt-1 text-sm text-gray-900 font-mono">{reservation.reservationid || reservation.id}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Created Time</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(reservation.createTime)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Start Date & Time</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(reservation.reserveDate)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">End Date & Time</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(reservation.endDate)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Duration</label>
              <p className="mt-1 text-sm text-gray-900">
                {formatDuration(reservation.duration, reservation.durationunit)}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Price</label>
              <p className="mt-1 text-sm text-gray-900 font-semibold">{formatCurrency(reservation.price)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Refund Issued</label>
              <p className="mt-1 text-sm text-gray-900">
                {reservation.REFUNDISSUED === true ? (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Yes
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                    No
                  </span>
                )}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Status</label>
              <p className="mt-1 text-sm text-gray-900">{reservation.status || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Price State</label>
              <p className="mt-1 text-sm text-gray-900">{reservation.price_state || 'N/A'}</p>
            </div>
            {reservation.reason && (
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-500">Cancellation Reason</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.reason}</p>
              </div>
            )}
          </div>
        </div>

        {/* Booker Details */}
        {reservation.bookerData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <UserIcon className="h-5 w-5 mr-2" />
              Booker Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          <div>
              <label className="block text-sm font-medium text-gray-500">Full Name</label>
              <p className="mt-1 text-sm text-gray-900">
                {reservation.bookerData?.full_name || 'N/A'}
              </p>
            </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.email || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Phone</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.phone || 'N/A'}</p>
              </div>
                          <div>
              <label className="block text-sm font-medium text-gray-500">Vehicle Number</label>
              <p className="mt-1 text-sm text-gray-900">{reservation.vehicleData?.number || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Vehicle Model</label>
              <p className="mt-1 text-sm text-gray-900">
                {reservation.vehicleData?.model || 'N/A'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Vehicle Type</label>
              <p className="mt-1 text-sm text-gray-900">
                {getVehicleTypeFromListingCategories(reservation.vehicleData?.type, listingCategories)}
              </p>
            </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Account Number</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.account_number || 'N/A'}</p>
              </div>
                          <div>
              <label className="block text-sm font-medium text-gray-500">Payment Type</label>
              <p className="mt-1 text-sm text-gray-900">{reservation.type || 'N/A'}</p>
            </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address 1</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.address1 || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address 2</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.address2 || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address 3</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.address3 || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">City</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.city || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">State</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.state || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Zipcode</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.bookerData.zipcode || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Parking Space Details */}
        {reservation.spaceData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2" />
              Parking Space Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Space Code</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.spaceCodeNo || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Type</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.type || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address 1</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.address1 || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address 2</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.address2 || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">City</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.city || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">State</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.state || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Zipcode</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.zipcode || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Is Active</label>
                <p className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    reservation.spaceData.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {reservation.spaceData.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Available</label>
                <p className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    reservation.spaceData.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {reservation.spaceData.available ? 'Available' : 'Not Available'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Available Slots</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.availableSlots || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Price</label>
                <p className="mt-1 text-sm text-gray-900 font-semibold">{formatCurrency(reservation.spaceData.price)}</p>
              </div>
              {reservation.spaceData.description && (
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-500">Description</label>
                  <p className="mt-1 text-sm text-gray-900">{reservation.spaceData.description}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Owner Details */}
        {reservation.ownerData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              Owner Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Full Name</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.fullName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.email || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Phone</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.phone || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Business Name</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.business_name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Is Verified</label>
                <p className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    reservation.ownerData.isVerified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {reservation.ownerData.isVerified ? 'Verified' : 'Not Verified'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address 1</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.address1 || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address 2</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.address2 || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address 3</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.address3 || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">City</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.city || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">State</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.state || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Zipcode</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.ownerData.zipcode || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Status Transition History */}
        {reservation.statusHistory && reservation.statusHistory.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Status History</h2>
            <div className="space-y-3">
              {reservation.statusHistory.map((transition, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColorClass(transition.state)}`}>
                      {getStatusIcon(transition.state)}
                      <span className="ml-1">{getStatusDisplayName(transition.state)}</span>
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      {getStatusTransitionExplanation(transition.prev_state, transition.reason, transition.state)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {transition.timestamp ? formatDate(transition.timestamp) : 'Unknown time'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Updated At</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(reservation.updatedAt)}</p>
            </div>
            {reservation.couponBought && reservation.couponBought.length > 0 && (
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-500">Coupons Bought</label>
                <div className="mt-1 space-y-2">
                  {reservation.couponBought.map((coupon, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-900">{JSON.stringify(coupon)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationDetail; 